import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
    Users, 
    CalendarCheck, 
    SignOut, 
    Plus, 
    ChartLine,
    User,
    CalendarBlank,
    Exam,
    MagnifyingGlass,
    Archive
} from '@phosphor-icons/react';
import StudentList from '../components/StudentList';
import AddStudentModal from '../components/AddStudentModal';
import StudentLessonCard from '../components/StudentLessonCard';
import LessonScheduler from '../components/LessonScheduler';
import { DashboardSkeleton } from '../components/Skeletons';
import DailyQuestion from '../components/DailyQuestion';
import LessonSummaryModal from '../components/LessonSummaryModal';
import ReferralGift from '../components/ReferralGift';
import { InstallAppButton } from '../components/InstallAppPrompt';
import { ChatButton } from '../components/ChatModal';
import PushOptInCard from '../components/PushOptInCard';
import NotificationSettingsButton from '../components/NotificationSettings';
import NotificationBell from '../components/NotificationBell';
import NextStudentCard from '../components/NextStudentCard';
import ExamSchedule from '../components/ExamSchedule';
import AnimatedNumber from '../components/AnimatedNumber';
import AnimatedProgressBar from '../components/AnimatedProgressBar';
import { RevealStagger } from '../components/Reveal';
import LessonCardErrorBoundary from '../components/LessonCardErrorBoundary';
import HomeTab from '../components/HomeTab';
import BottomNav from '../components/BottomNav';
import AccountTab from '../components/AccountTab';
import StudentCancelLessonsModal from '../components/StudentCancelLessonsModal';
import LessonConfirmModal from '../components/LessonConfirmModal';
import StudentOnboardingModal from '../components/StudentOnboardingModal';
import DrivenCheckModal from '../components/DrivenCheckModal';
import AdministratieModal from '../components/AdministratieModal';
import useBackGuard from '../hooks/useBackGuard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_lesson-card-manager/artifacts/zd0x6jcg_c982a405-739a-4717-a6a0-d9774dc13353_20251118_231830_0000.png";

export default function DashboardPage() {
    const { user, logout, isInstructor } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [stats, setStats] = useState(null);
    const [students, setStudents] = useState([]);
    const [scheduledLessons, setScheduledLessons] = useState([]);
    const [selectedStudent, setSelectedStudent] = useState(null);
    const [showAddStudent, setShowAddStudent] = useState(false);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('home');
    const [searchQuery, setSearchQuery] = useState('');
    const [showArchived, setShowArchived] = useState(false);
    const [lessonCounts, setLessonCounts] = useState({});
    const [plannedCounts, setPlannedCounts] = useState({});
    const [showDailyQuestion, setShowDailyQuestion] = useState(false);
    const [showLessonSummary, setShowLessonSummary] = useState(false);
    const [showCancelModal, setShowCancelModal] = useState(false);
    const [confirmModalLesson, setConfirmModalLesson] = useState(null);
    const [dismissedConfirmIds, setDismissedConfirmIds] = useState(() => new Set());
    const [drivenCheckId, setDrivenCheckId] = useState(null);
    const [needsOnboarding, setNeedsOnboarding] = useState(false);
    const [administratieOpen, setAdministratieOpen] = useState(false);
    const [calendarConnected, setCalendarConnected] = useState(false);

    useEffect(() => {
        fetchData();
    }, [showArchived]);

    // Show daily question for students — but wait until the lesson-confirmation
    // memo has been handled (it's more urgent / time-sensitive).
    useEffect(() => {
        if (!user || user.role !== 'student') return;
        if (loading) return;
        if (needsOnboarding) return;   // wait until profile is filled in
        if (confirmModalLesson) return;
        // Defer until scheduledLessons has been fetched at least once, so we
        // don't accidentally pre-empt a pending-lesson modal.
        const now = new Date();
        const hasPending = (scheduledLessons || []).some(l => {
            if (l.confirmation_status !== 'pending') return false;
            if (dismissedConfirmIds.has(l.id)) return false;
            try {
                const dt = new Date(`${l.date}T${l.time || '00:00'}:00`);
                return dt > now;
            } catch { return false; }
        });
        if (hasPending) return;

        const key = `cbr_question_${new Date().toISOString().split('T')[0]}`;
        if (!sessionStorage.getItem(key)) {
            setShowDailyQuestion(true);
        } else {
            const sumKey = `lesson_summary_shown_${user.id}`;
            if (!sessionStorage.getItem(sumKey)) {
                setShowLessonSummary(true);
            }
        }
    }, [user, loading, confirmModalLesson, scheduledLessons, dismissedConfirmIds, needsOnboarding]);

    // Handle PWA shortcut deep-links (/dashboard?tab=agenda | view=card | open=chat | action=cancel)
    useEffect(() => {
        if (loading || !user) return;
        const params = new URLSearchParams(location.search);
        const tab = params.get('tab');
        const view = params.get('view');
        const open = params.get('open');
        const action = params.get('action');

        if (!tab && !view && !open && !action) return;

        // Instructor tabs: home / leerlingen / agenda / examens / account
        // Student tabs: home / leskaart / account
        const validInstructorTabs = ['home', 'leerlingen', 'agenda', 'examens', 'account'];
        const validStudentTabs = ['home', 'leskaart', 'account'];
        if (tab) {
            if (isInstructor && validInstructorTabs.includes(tab)) setActiveTab(tab);
            else if (!isInstructor && validStudentTabs.includes(tab)) setActiveTab(tab);
        }

        // Auto-select own leskaart for students (view=card only)
        if (!isInstructor && view === 'card' && students.length > 0 && !selectedStudent) {
            const own = students.find(s => s.email === user.email) || students[0];
            if (own) setSelectedStudent(own);
        }

        // Open chat — fires on next tick so listener is mounted
        if (open === 'chat') {
            window.setTimeout(() => window.dispatchEvent(new CustomEvent('qr:open-chat')), 200);
        }

        // Instructor deep-link to a driven-check for a specific scheduled lesson
        if (open === 'driven-check' && isInstructor) {
            const id = params.get('id');
            if (id) window.setTimeout(() => window.dispatchEvent(new CustomEvent('qr:open-driven-check', { detail: { id } })), 200);
        }

        // Instructor deep-link to administratie overview (from payment-reminder notification)
        if (open === 'administratie' && isInstructor) {
            window.setTimeout(() => setAdministratieOpen(true), 200);
        }

        // Trigger cancel-lesson modal globally (handled by DashboardPage listener)
        if (action === 'cancel' && !isInstructor) {
            window.setTimeout(() => window.dispatchEvent(new CustomEvent('qr:open-cancel')), 200);
        }

        // Clean up the URL so a reload doesn't re-trigger the action
        if (tab || view || open || action) {
            navigate('/dashboard', { replace: true });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loading, user, students.length]);

    const fetchData = async () => {
        try {
            const [statsRes, studentsRes] = await Promise.all([
                axios.get(`${API}/dashboard/stats`, { withCredentials: true }),
                axios.get(`${API}/students?include_archived=${showArchived}`, { withCredentials: true })
            ]);
            setStats(statsRes.data);
            setStudents(studentsRes.data);

            // Fetch lesson counts per student: combine registered lesson history + past scheduled lessons
            // (60 min = 1 les). Dedupe by date to avoid counting both history + scheduled for same date.
            if (studentsRes.data.length > 0) {
                const [lessonsRes, scheduledRes] = await Promise.all([
                    axios.get(`${API}/lessons`, { withCredentials: true }),
                    axios.get(`${API}/scheduled-lessons`, { withCredentials: true }).catch(() => ({ data: [] })),
                ]);
                setScheduledLessons(scheduledRes.data || []);
                const now = new Date();
                // Group by student -> { driven: Map<date, hrs>, planned: Map<date, hrs> }
                const perStudent = {};
                const ensure = (sid) => {
                    if (!perStudent[sid]) perStudent[sid] = { driven: {}, planned: {} };
                    return perStudent[sid];
                };
                const addTo = (bucket, date, durationMin) => {
                    if (!date) return;
                    const hours = (durationMin || 60) / 60;
                    if (!bucket[date] || bucket[date] < hours) bucket[date] = hours;
                };
                (lessonsRes.data || []).forEach(l => {
                    if (!l.student_id) return;
                    addTo(ensure(l.student_id).driven, l.date, l.duration_minutes);
                });
                (scheduledRes.data || []).forEach(l => {
                    if (!l.student_id) return;
                    if (l.status === 'cancelled') return;
                    try {
                        const end = new Date(`${l.date}T${l.time || '09:00'}:00`);
                        end.setMinutes(end.getMinutes() + (l.duration_minutes || 60));
                        const bucket = end <= now ? ensure(l.student_id).driven : ensure(l.student_id).planned;
                        addTo(bucket, l.date, l.duration_minutes);
                    } catch (e) { /* skip invalid */ }
                });
                const counts = {};
                const plannedMap = {};
                Object.entries(perStudent).forEach(([sid, { driven, planned }]) => {
                    const totalDriven = Object.values(driven).reduce((a, b) => a + b, 0);
                    const totalPlanned = Object.values(planned).reduce((a, b) => a + b, 0);
                    // Store PURE registered/past-scheduled counts (no initial_lessons).
                    // The consumers (LessonBudgetCard / StudentList) subtract initial_lessons
                    // from the package instead of adding it to "gereden".
                    counts[sid] = Math.round(totalDriven * 10) / 10;
                    plannedMap[sid] = Math.round(totalPlanned * 10) / 10;
                });
                setLessonCounts(counts);
                setPlannedCounts(plannedMap);
            }
        } catch (e) {
            console.error('Error fetching data:', e);
        } finally {
            setLoading(false);
        }
    };

    const checkCalendarStatus = async () => {
        try {
            const res = await axios.get(`${API}/oauth/calendar/status`, { withCredentials: true });
            setCalendarConnected(res.data.connected);
        } catch (e) {
            // Not connected
        }
    };

    const handleConnectCalendar = async () => {
        try {
            const res = await axios.get(`${API}/oauth/calendar/connect`, { withCredentials: true });
            window.location.href = res.data.authorization_url;
        } catch (e) {
            console.error('Error connecting calendar:', e);
        }
    };

    const handleDisconnectCalendar = async () => {
        try {
            await axios.post(`${API}/oauth/calendar/disconnect`, {}, { withCredentials: true });
            setCalendarConnected(false);
        } catch (e) {
            console.error('Error disconnecting calendar:', e);
        }
    };

    const handleLogout = async () => {
        await logout();
        navigate('/login');
    };

    const handleStudentAdded = () => {
        setShowAddStudent(false);
        fetchData();
    };

    const handleStudentSelect = (student) => {
        setSelectedStudent(student);
    };

    const handleBackToList = () => {
        setSelectedStudent(null);
        fetchData();
    };

    // Intercept Android back-gesture / browser back: when a leskaart is open,
    // close it instead of exiting the PWA.
    useBackGuard(!!selectedStudent, handleBackToList);

    // Global student-cancel handler — works from Home tile, chat quick-reply,
    // PWA shortcut (?action=cancel), or any other entry point without needing
    // the leskaart to be open.
    useEffect(() => {
        if (isInstructor) return;
        const openBulkCancel = () => setShowCancelModal(true);
        window.addEventListener('qr:open-cancel', openBulkCancel);
        return () => window.removeEventListener('qr:open-cancel', openBulkCancel);
    }, [isInstructor]);

    // Onboarding-check for self-registered students without a `students` record
    useEffect(() => {
        if (!user || user.role !== 'student') return;
        axios.get(`${API}/students/me/onboarding-status`, { withCredentials: true })
            .then(({ data }) => setNeedsOnboarding(!!data?.needs_onboarding))
            .catch(() => {});
    }, [user]);

    // Instructor-only: driven-check event from NotificationBell or ?open=driven-check deep-link
    useEffect(() => {
        if (!isInstructor) return;
        const handler = (e) => setDrivenCheckId(e?.detail?.id || null);
        window.addEventListener('qr:open-driven-check', handler);
        return () => window.removeEventListener('qr:open-driven-check', handler);
    }, [isInstructor]);

    // Auto-show the confirm-lesson modal whenever a student opens the app and
    // has an unconfirmed upcoming lesson they haven't dismissed yet.
    useEffect(() => {
        if (isInstructor || loading || !user) return;
        if (confirmModalLesson || showCancelModal) return;
        const now = new Date();
        const pending = (scheduledLessons || [])
            .filter(l => {
                if (l.confirmation_status !== 'pending') return false;
                if (dismissedConfirmIds.has(l.id)) return false;
                try {
                    const dt = new Date(`${l.date}T${l.time || '00:00'}:00`);
                    return dt > now;
                } catch { return false; }
            })
            .sort((a, b) =>
                (a.date || '').localeCompare(b.date || '') ||
                (a.time || '').localeCompare(b.time || '')
            );
        if (pending.length > 0) {
            setConfirmModalLesson(pending[0]);
        }
    }, [scheduledLessons, isInstructor, loading, user, confirmModalLesson, showCancelModal, dismissedConfirmIds]);

    // When viewing any non-home tab, back returns to Home instead of closing the PWA.
    // Kept active even while a leskaart is open — its sentinel sits *below* the
    // leskaart's sentinel in history, so the leskaart pops first, then this one.
    useBackGuard(activeTab !== 'home', () => setActiveTab('home'));

    const handleDeleteStudent = async (studentId) => {
        if (!window.confirm('Weet je zeker dat je deze leerling wilt verwijderen? Dit kan niet ongedaan worden.')) return;
        try {
            await axios.delete(`${API}/students/${studentId}`, { withCredentials: true });
            fetchData();
        } catch (e) {
            console.error('Error deleting student:', e);
        }
    };

    const handleArchiveStudent = async (studentId) => {
        try {
            await axios.put(`${API}/students/${studentId}/archive`, {}, { withCredentials: true });
            fetchData();
        } catch (e) {
            console.error('Error archiving student:', e);
        }
    };

    // Filter students by search
    const filteredStudents = useMemo(() => {
        if (!searchQuery.trim()) return students;
        const q = searchQuery.toLowerCase();
        return students.filter(s => 
            s.name.toLowerCase().includes(q) || 
            s.email.toLowerCase().includes(q) ||
            (s.phone && s.phone.includes(q))
        );
    }, [students, searchQuery]);

    if (loading) {
        return <DashboardSkeleton />;
    }

    return (
        <div className="min-h-screen bg-[#1A202C] relative">
            {/* Header */}
            <header className="bg-[#2D3748] border-b border-[#4A5568] sticky top-0 z-50 animate-fade-in-down">
                <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center h-14 sm:h-16 gap-2">
                        <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-shrink">
                            <img src={LOGO_URL} alt="Rijschool AutoPilot" className="w-8 h-8 sm:w-10 sm:h-10 object-contain flex-shrink-0" />
                            <h1 className="hidden sm:block text-lg sm:text-xl font-semibold text-white truncate" style={{ fontFamily: 'Outfit, sans-serif' }}>
                                Rijschool <span className="text-[#D4A537]">AutoPilot</span>
                            </h1>
                            <h1 className="sm:hidden text-base font-semibold text-[#D4A537] truncate" style={{ fontFamily: 'Outfit, sans-serif' }}>
                                AutoPilot
                            </h1>
                        </div>
                        <div className="flex items-center gap-0.5 sm:gap-2 flex-shrink-0">
                            <NotificationBell />
                            <ChatButton role={user?.role} />
                            {/* Push-permission settings live in the Account tab to avoid duplicate bell icons in the header */}
                            <div className="hidden">
                                <NotificationSettingsButton role={user?.role} />
                            </div>
                            <InstallAppButton />
                            {/* Desktop: name + role badge inline */}
                            <div className="hidden md:flex items-center gap-2 text-sm text-gray-300 ml-2 mr-1">
                                <User size={18} className="text-[#D4A537]" />
                                <span className="truncate max-w-[140px]">{user?.name}</span>
                                <span className="px-2 py-0.5 bg-[#D4A537]/20 text-[#D4A537] rounded text-xs font-medium whitespace-nowrap">
                                    {user?.role === 'instructor' ? 'Instructeur' : 'Leerling'}
                                </span>
                            </div>
                            {/* Mobile: tiny avatar/initial pill */}
                            <div className="md:hidden flex items-center pl-1 pr-1" title={user?.name}>
                                <div className="w-8 h-8 rounded-full bg-[#D4A537]/15 border border-[#D4A537]/40 flex items-center justify-center text-[#D4A537] font-bold text-xs">
                                    {(user?.name || '?').trim().slice(0, 1).toUpperCase()}
                                </div>
                            </div>
                            {/* Desktop: full button */}
                            <Button variant="ghost" size="sm" onClick={handleLogout} className="hidden md:inline-flex text-gray-400 hover:text-white hover:bg-[#3D4A5C]" data-testid="logout-button">
                                <SignOut size={18} className="mr-1" />
                                Uitloggen
                            </Button>
                            {/* Mobile: icon-only logout */}
                            <button
                                onClick={handleLogout}
                                className="md:hidden p-2 rounded-lg text-gray-400 hover:text-white hover:bg-[#3D4A5C] transition-colors"
                                data-testid="logout-button-mobile"
                                aria-label="Uitloggen"
                                title="Uitloggen"
                            >
                                <SignOut size={18} />
                            </button>
                        </div>
                    </div>
                </div>
            </header>

            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 pb-24 md:pb-8 relative z-10">
                {selectedStudent ? (
                    <div className="animate-fade-in-up">
                        <LessonCardErrorBoundary>
                            <StudentLessonCard student={selectedStudent} onBack={handleBackToList} isInstructor={isInstructor} />
                        </LessonCardErrorBoundary>
                    </div>
                ) : (
                    <>
                        {/* Desktop-only stat cards & next student preview (hidden on home/account tabs) */}
                        {activeTab !== 'home' && activeTab !== 'account' && (
                            <>
                                <RevealStagger className="hidden md:grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                                    {isInstructor ? (
                                        <>
                                            <Card className="bg-[#2D3748] border-[#4A5568] hover-lift" data-testid="stats-students-card">
                                                <CardHeader className="flex flex-row items-center justify-between pb-2">
                                                    <CardTitle className="text-sm font-medium text-gray-400">Leerlingen</CardTitle>
                                                    <Users size={20} className="text-[#D4A537]" />
                                                </CardHeader>
                                                <CardContent>
                                                    <div className="text-3xl font-bold text-white">
                                                        <AnimatedNumber value={stats?.students_count || 0} />
                                                    </div>
                                                </CardContent>
                                            </Card>
                                            <Card className="bg-[#2D3748] border-[#4A5568] hover-lift" data-testid="stats-lessons-card">
                                                <CardHeader className="flex flex-row items-center justify-between pb-2">
                                                    <CardTitle className="text-sm font-medium text-gray-400">Totaal Lessen</CardTitle>
                                                    <CalendarCheck size={20} className="text-[#D4A537]" />
                                                </CardHeader>
                                                <CardContent>
                                                    <div className="text-3xl font-bold text-white">
                                                        <AnimatedNumber value={stats?.lessons_count || 0} />
                                                    </div>
                                                </CardContent>
                                            </Card>
                                            <Card className="bg-[#2D3748] border-[#4A5568] hover-lift">
                                                <CardHeader className="flex flex-row items-center justify-between pb-2">
                                                    <CardTitle className="text-sm font-medium text-gray-400">Actieve Leerlingen</CardTitle>
                                                    <ChartLine size={20} className="text-[#D4A537]" />
                                                </CardHeader>
                                                <CardContent>
                                                    <div className="text-3xl font-bold text-white">
                                                        <AnimatedNumber value={students.filter(s => s.status !== 'archived').length} />
                                                    </div>
                                                </CardContent>
                                            </Card>
                                        </>
                                    ) : (
                                        <>
                                            <Card className="bg-[#2D3748] border-[#4A5568] hover-lift" data-testid="stats-lessons-card">
                                                <CardHeader className="flex flex-row items-center justify-between pb-2">
                                                    <CardTitle className="text-sm font-medium text-gray-400">Mijn Lessen</CardTitle>
                                                    <CalendarCheck size={20} className="text-[#D4A537]" />
                                                </CardHeader>
                                                <CardContent>
                                                    <div className="text-3xl font-bold text-white">
                                                        <AnimatedNumber value={stats?.lessons_count || 0} />
                                                    </div>
                                                </CardContent>
                                            </Card>
                                            <Card className="bg-[#2D3748] border-[#4A5568] hover-lift col-span-2" data-testid="stats-progress-card">
                                                <CardHeader className="flex flex-row items-center justify-between pb-2">
                                                    <CardTitle className="text-sm font-medium text-gray-400">Totale Voortgang</CardTitle>
                                                    <ChartLine size={20} className="text-[#D4A537]" />
                                                </CardHeader>
                                                <CardContent>
                                                    <div className="flex items-center gap-4">
                                                        <div className="text-3xl font-bold text-white">
                                                            <AnimatedNumber value={stats?.progress || 0} suffix="%" />
                                                        </div>
                                                        <AnimatedProgressBar pct={stats?.progress || 0} barClassName="bg-[#D4A537]" height={8} duration={1400} />
                                                    </div>
                                                </CardContent>
                                            </Card>
                                        </>
                                    )}
                                </RevealStagger>
                                {isInstructor && (
                                    <div className="hidden md:block mb-8 animate-fade-in-up" style={{ animationDelay: '0.35s' }}>
                                        <NextStudentCard students={students} onSelectStudent={handleStudentSelect} />
                                    </div>
                                )}
                            </>
                        )}

                        {/* Main Content */}
                        {isInstructor ? (
                            <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
                                {/* Top tab list — desktop only (mobile uses bottom nav) */}
                                <TabsList className="hidden md:inline-flex bg-[#2D3748] border border-[#4A5568]">
                                    <TabsTrigger value="home" className="data-[state=active]:bg-[#D4A537] data-[state=active]:text-[#1A202C]" data-testid="tab-home">
                                        Home
                                    </TabsTrigger>
                                    <TabsTrigger value="leerlingen" className="data-[state=active]:bg-[#D4A537] data-[state=active]:text-[#1A202C]" data-testid="tab-students">
                                        <Users size={18} className="mr-2" />
                                        Leerlingen
                                    </TabsTrigger>
                                    <TabsTrigger value="agenda" className="data-[state=active]:bg-[#D4A537] data-[state=active]:text-[#1A202C]" data-testid="tab-agenda">
                                        <CalendarBlank size={18} className="mr-2" />
                                        Lesplanning
                                    </TabsTrigger>
                                    <TabsTrigger value="examens" className="data-[state=active]:bg-[#D4A537] data-[state=active]:text-[#1A202C]" data-testid="tab-examens">
                                        <Exam size={18} className="mr-2" weight="fill" />
                                        Examens
                                    </TabsTrigger>
                                    <TabsTrigger value="account" className="data-[state=active]:bg-[#D4A537] data-[state=active]:text-[#1A202C]" data-testid="tab-account">
                                        <User size={18} className="mr-2" />
                                        Account
                                    </TabsTrigger>
                                </TabsList>

                                <TabsContent value="home">
                                    <HomeTab
                                        user={user}
                                        isInstructor
                                        students={students}
                                        scheduledLessons={scheduledLessons}
                                        stats={stats}
                                        onGoTab={(t) => setActiveTab(t)}
                                        onAddStudent={() => setShowAddStudent(true)}
                                        onSelectStudent={handleStudentSelect}
                                        onOpenAdministratie={() => setAdministratieOpen(true)}
                                    />
                                </TabsContent>

                                <TabsContent value="leerlingen">
                                    <div className="bg-[#2D3748] rounded-lg border border-[#4A5568]">
                                        <div className="p-6 border-b border-[#4A5568]">
                                            <div className="flex justify-between items-center mb-4">
                                                <div>
                                                    <h2 className="text-xl font-semibold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                                                        Mijn Leerlingen
                                                    </h2>
                                                    <p className="text-sm text-gray-400 mt-1">Beheer en bekijk de voortgang van je leerlingen</p>
                                                </div>
                                                <Button onClick={() => setShowAddStudent(true)} className="bg-[#D4A537] text-[#1A202C] hover:bg-[#E5B84A] font-semibold btn-ripple tap-bounce" data-testid="add-student-button">
                                                    <Plus size={18} className="mr-2" />
                                                    Leerling Toevoegen
                                                </Button>
                                            </div>
                                            {/* Search and filter */}
                                            <div className="flex gap-3">
                                                <div className="relative flex-1">
                                                    <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                                                    <Input
                                                        placeholder="Zoek op naam, email of telefoon..."
                                                        value={searchQuery}
                                                        onChange={(e) => setSearchQuery(e.target.value)}
                                                        className="pl-10 bg-[#1A202C] border-[#4A5568] text-white placeholder:text-gray-500"
                                                        data-testid="search-students-input"
                                                    />
                                                </div>
                                                <Button
                                                    variant="outline"
                                                    onClick={() => setShowArchived(!showArchived)}
                                                    className={`border-[#4A5568] ${showArchived ? 'bg-[#D4A537]/20 text-[#D4A537] border-[#D4A537]' : 'text-gray-400 hover:bg-[#3D4A5C]'}`}
                                                    data-testid="toggle-archived-button"
                                                >
                                                    <Archive size={18} className="mr-2" />
                                                    Archief
                                                </Button>
                                            </div>
                                        </div>

                                        {filteredStudents.length === 0 ? (
                                            <div className="empty-state py-16">
                                                <div className="w-24 h-24 bg-[#3D4A5C] rounded-full flex items-center justify-center mx-auto mb-6">
                                                    <Users size={48} className="text-[#D4A537]" />
                                                </div>
                                                <h3 className="text-lg font-medium text-white mb-2">
                                                    {searchQuery ? 'Geen resultaten' : 'Nog geen leerlingen'}
                                                </h3>
                                                <p className="text-gray-400 mb-6">
                                                    {searchQuery ? 'Probeer een andere zoekterm' : 'Voeg je eerste leerling toe om te beginnen'}
                                                </p>
                                                {!searchQuery && (
                                                    <Button onClick={() => setShowAddStudent(true)} className="bg-[#D4A537] text-[#1A202C] hover:bg-[#E5B84A] font-semibold">
                                                        <Plus size={18} className="mr-2" />
                                                        Eerste Leerling Toevoegen
                                                    </Button>
                                                )}
                                            </div>
                                        ) : (
                                            <StudentList 
                                                students={filteredStudents}
                                                onSelect={handleStudentSelect}
                                                isInstructor={isInstructor}
                                                onDelete={handleDeleteStudent}
                                                onArchive={handleArchiveStudent}
                                                lessonCounts={lessonCounts}
                                                plannedCounts={plannedCounts}
                                            />
                                        )}
                                    </div>
                                </TabsContent>

                                <TabsContent value="agenda">
                                    <LessonScheduler students={students.filter(s => s.status !== 'archived')} onLessonAdded={fetchData} />
                                </TabsContent>

                                <TabsContent value="examens">
                                    <ExamSchedule students={students} onSelectStudent={handleStudentSelect} />
                                </TabsContent>

                                <TabsContent value="account">
                                    <AccountTab
                                        user={user}
                                        isInstructor
                                        onOpenNotifications={() => {
                                            document.querySelector('[data-testid=notification-settings-button]')?.click();
                                        }}
                                        onOpenInstall={() => {
                                            document.querySelector('[data-testid=install-app-button]')?.click();
                                        }}
                                        onLogout={handleLogout}
                                    />
                                </TabsContent>
                            </Tabs>
                        ) : (
                            <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
                                <TabsList className="hidden md:inline-flex bg-[#2D3748] border border-[#4A5568]">
                                    <TabsTrigger value="home" className="data-[state=active]:bg-[#D4A537] data-[state=active]:text-[#1A202C]" data-testid="tab-home">
                                        Home
                                    </TabsTrigger>
                                    <TabsTrigger value="leskaart" className="data-[state=active]:bg-[#D4A537] data-[state=active]:text-[#1A202C]" data-testid="tab-leskaart-list">
                                        Mijn leskaart
                                    </TabsTrigger>
                                    <TabsTrigger value="account" className="data-[state=active]:bg-[#D4A537] data-[state=active]:text-[#1A202C]" data-testid="tab-account">
                                        <User size={18} className="mr-2" />
                                        Account
                                    </TabsTrigger>
                                </TabsList>

                                <TabsContent value="home">
                                    <HomeTab
                                        user={user}
                                        isInstructor={false}
                                        students={students}
                                        scheduledLessons={scheduledLessons}
                                        stats={stats}
                                        onGoTab={(t) => {
                                            if (t === 'leskaart' && students.length > 0) {
                                                const own = students.find(s => s.email === user.email) || students[0];
                                                setSelectedStudent(own);
                                            } else {
                                                setActiveTab(t);
                                            }
                                        }}
                                    />
                                </TabsContent>

                                <TabsContent value="leskaart">
                                    <div className="bg-[#2D3748] rounded-lg border border-[#4A5568]">
                                        <div className="p-6 border-b border-[#4A5568]">
                                            <h2 className="text-xl font-semibold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>Mijn Leskaart</h2>
                                            <p className="text-sm text-gray-400 mt-1">Bekijk je voortgang en lesgeschiedenis</p>
                                        </div>
                                        {students.length === 0 ? (
                                            <div className="empty-state py-16">
                                                <div className="w-24 h-24 bg-[#3D4A5C] rounded-full flex items-center justify-center mx-auto mb-6">
                                                    <CalendarCheck size={48} className="text-[#D4A537]" />
                                                </div>
                                                <h3 className="text-lg font-medium text-white mb-2">Nog geen leskaart</h3>
                                                <p className="text-gray-400">Je instructeur moet je nog toevoegen aan het systeem</p>
                                            </div>
                                        ) : (
                                            <StudentList students={students} onSelect={handleStudentSelect} isInstructor={isInstructor} lessonCounts={lessonCounts} plannedCounts={plannedCounts} />
                                        )}
                                    </div>
                                </TabsContent>

                                <TabsContent value="account">
                                    <AccountTab
                                        user={user}
                                        isInstructor={false}
                                        onOpenNotifications={() => {
                                            document.querySelector('[data-testid=notification-settings-button]')?.click();
                                        }}
                                        onOpenInstall={() => {
                                            document.querySelector('[data-testid=install-app-button]')?.click();
                                        }}
                                        onLogout={handleLogout}
                                    />
                                </TabsContent>
                            </Tabs>
                        )}
                    </>
                )}
            </main>

            {/* Mobile bottom navigation — hidden when a leskaart detail is open */}
            {!selectedStudent && (
                <BottomNav
                    activeTab={activeTab}
                    onChange={(t) => {
                        if (!isInstructor && t === 'leskaart' && students.length > 0) {
                            const own = students.find(s => s.email === user.email) || students[0];
                            setSelectedStudent(own);
                            return;
                        }
                        setActiveTab(t);
                    }}
                    role={user?.role}
                />
            )}

            {showAddStudent && <AddStudentModal onClose={() => setShowAddStudent(false)} onSuccess={handleStudentAdded} />}
            {showDailyQuestion && (
                <DailyQuestion onComplete={() => {
                    const key = `cbr_question_${new Date().toISOString().split('T')[0]}`;
                    sessionStorage.setItem(key, 'done');
                    setShowDailyQuestion(false);
                    // Chain: after daily CBR question, show lesson summary if any
                    if (user && user.role === 'student') {
                        const sumKey = `lesson_summary_shown_${user.id}`;
                        if (!sessionStorage.getItem(sumKey)) {
                            setShowLessonSummary(true);
                        }
                    }
                }} />
            )}
            {showLessonSummary && user?.role === 'student' && (
                <LessonSummaryModal
                    studentName={user.name}
                    onClose={() => {
                        sessionStorage.setItem(`lesson_summary_shown_${user.id}`, 'done');
                        setShowLessonSummary(false);
                    }}
                />
            )}
            {user?.role === 'student' && (
                <ReferralGift studentName={user.name} />
            )}
            {user?.role === 'student' && (
                <StudentCancelLessonsModal
                    open={showCancelModal}
                    onClose={() => setShowCancelModal(false)}
                    studentName={user.name}
                />
            )}
            {user?.role === 'student' && confirmModalLesson && (
                <LessonConfirmModal
                    open={!!confirmModalLesson}
                    lesson={confirmModalLesson}
                    studentName={user.name}
                    onClose={(confirmed) => {
                        if (!confirmed && confirmModalLesson) {
                            // Mark as dismissed for this session so it doesn't re-open
                            setDismissedConfirmIds(prev => new Set([...prev, confirmModalLesson.id]));
                        }
                        setConfirmModalLesson(null);
                        // Refresh the lesson list to reflect new confirmation_status
                        if (confirmed) fetchData();
                    }}
                />
            )}
            {isInstructor && drivenCheckId && (
                <DrivenCheckModal
                    open={!!drivenCheckId}
                    lessonId={drivenCheckId}
                    onClose={() => setDrivenCheckId(null)}
                    onLogged={() => { setDrivenCheckId(null); fetchData(); }}
                />
            )}
            {isInstructor && (
                <AdministratieModal
                    open={administratieOpen}
                    onClose={() => setAdministratieOpen(false)}
                    onSelectStudent={(sid) => {
                        const s = students.find(x => x.id === sid);
                        if (s) handleStudentSelect(s);
                    }}
                />
            )}
            {user?.role === 'student' && needsOnboarding && (
                <StudentOnboardingModal
                    user={user}
                    onCompleted={() => { setNeedsOnboarding(false); fetchData(); }}
                />
            )}
            <PushOptInCard role={user?.role} />
        </div>
    );
}
